import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, Users, Wifi, WifiOff, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useTranslator } from "@/hooks/useTranslator";
import { TranscriptPanel } from "@/components/TranscriptPanel";
import { QuickPhrasesSheet } from "@/components/QuickPhrasesSheet";
import { TopBar } from "@/components/TopBar";
import { LANGS, WS_BASE, getRoom, getRoomMessages } from "@/lib/api";

const RECONNECT_DELAY_MS = 1500;
const QR_SIZE_PX = 220;

const RolePicker = ({ code, onPick }) => (
  <div className="flex h-full flex-col bg-[#0b132b] text-white" data-testid="room-role-picker">
    <TopBar title={`Sala ${code}`} />
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center gap-4 px-5 py-8 text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">Elige tu idioma · <span className="font-arabic normal-case tracking-normal">ختار اللغة ديالك</span></p>
      <button
        type="button"
        data-testid="pick-role-es-button"
        onClick={() => onPick("es")}
        className="h-20 rounded-2xl bg-blue-600 text-2xl font-extrabold transition-colors hover:bg-blue-500"
      >
        Hablo español
      </button>
      <button
        type="button"
        data-testid="pick-role-ar-button"
        onClick={() => onPick("ar")}
        className="font-arabic h-20 rounded-2xl bg-amber-500 text-3xl font-bold text-slate-950 transition-colors hover:bg-amber-400"
      >
        نهضر بالدارجة
      </button>
    </div>
  </div>
);

export default function RoomLive() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const rol = params.get("rol");
  const role = rol === "ar" || rol === "es" ? rol : null;
  const [peers, setPeers] = useState(0);
  const [connected, setConnected] = useState(false);
  const [qrOpen, setQrOpen] = useState(params.get("qr") !== "0");
  const wsRef = useRef(null);
  const { segments, addUtterance, addPhrase, setPending, upsert, setSegments } = useTranslator();

  const send = (data) => wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify(data));

  const speech = useSpeechRecognition({
    lang: LANGS[role || "es"].speech,
    onInterim: (t) => setPending(role, t),
    onFinal: (t, replace) =>
      addUtterance(
        role,
        t,
        {
          onPartial: (seg) => send({ type: "partial", ...seg }),
          onDone: (seg) => send({ type: "message", id: seg.id, source: seg.source, original: seg.original, translation: seg.translation }),
        },
        replace
      ),
  });

  useEffect(() => {
    if (!role) return;
    let alive = true;
    let ws;
    let retry;
    getRoom(code)
      .then(() => getRoomMessages(code))
      .then((msgs) => alive && setSegments(msgs.map((m) => ({ ...m, status: "done" }))))
      .catch(() => {
        toast.error("Sala no encontrada.");
        nav("/sala");
      });

    const connect = () => {
      ws = new WebSocket(`${WS_BASE}/rooms/${code}`);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (e) => {
        const d = JSON.parse(e.data);
        if (d.type === "presence") setPeers(d.peers);
        else if (d.type === "partial") upsert({ id: d.id, source: d.source, original: d.original, translation: d.translation, status: "translating" });
        else if (d.type === "message") upsert({ id: d.id, source: d.source, original: d.original, translation: d.translation, status: "done" });
      };
      ws.onclose = () => {
        setConnected(false);
        if (alive) retry = setTimeout(connect, RECONNECT_DELAY_MS);
      };
    };
    connect();
    return () => {
      alive = false;
      clearTimeout(retry);
      ws?.close();
    };
  }, [code, role, nav, setSegments, upsert]);

  if (!role) {
    return <RolePicker code={code} onPick={(r) => setParams({ rol: r, qr: "0" })} />;
  }

  const joinUrl = `${window.location.origin}/sala/${code}?qr=0`;
  const copy = () => navigator.clipboard?.writeText(joinUrl).then(() => toast.success("Enlace copiado"));

  const onMic = () => {
    if (!speech.supported) return toast.error("Este navegador no soporta reconocimiento de voz. Usa Chrome o Edge.");
    speech.toggle();
  };

  return (
    <div className="flex h-full flex-col bg-[#0b132b] text-white" data-testid="room-live-page">
      <TopBar title={`Sala ${code}`}>
        <span className="flex items-center gap-1 text-xs text-slate-300" data-testid="room-peers" title="Dispositivos conectados">
          <Users className="h-4 w-4" /> {peers}
        </span>
        <span data-testid="room-connection-status" className={connected ? "text-emerald-400" : "text-red-400"}>
          {connected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
        </span>
        <button
          type="button"
          data-testid="show-qr-button"
          onClick={() => setQrOpen(true)}
          className="flex h-10 items-center gap-2 rounded-full border border-slate-600 bg-slate-800/80 px-3 text-sm font-semibold hover:bg-slate-700"
        >
          <QrCode className="h-4 w-4 text-amber-400" /> QR
        </button>
        {role === "es" && <QuickPhrasesSheet onPick={(p) => { const s = addPhrase("es", p.es, p.ar); send({ type: "message", ...s }); }} />}
      </TopBar>

      <div className="min-h-0 flex-1">
        <TranscriptPanel
          lang={role}
          segments={segments}
          listening={speech.listening}
          onMic={onMic}
          fontScale={1.1}
          testId={`transcript-${role === "es" ? "spanish" : "darija"}-container`}
          micTestId={`mic-button-${role === "es" ? "spanish" : "darija"}`}
          accent={role === "es" ? "text-blue-300" : "text-amber-300"}
        />
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="border-slate-700 bg-[#1c2541] text-white sm:max-w-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle>Comparte esta sala</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300">
            Cada persona escanea el QR con la cámara de su móvil o introduce el código, elige su idioma y entra. Sin límite de
            dispositivos por sala.
          </p>
          <div className="mx-auto rounded-2xl bg-white p-3">
            <QRCodeSVG value={joinUrl} size={QR_SIZE_PX} level="M" data-testid="room-qr-code" />
          </div>
          <p className="text-center font-mono text-4xl font-extrabold tracking-[0.35em]" data-testid="room-code-display">
            {code}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="copy-join-link-button"
              onClick={copy}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-600 text-sm font-semibold hover:bg-slate-800"
            >
              <Copy className="h-4 w-4" /> Copiar enlace
            </button>
            <button
              type="button"
              data-testid="close-qr-button"
              onClick={() => setQrOpen(false)}
              className="h-12 flex-1 rounded-xl bg-amber-500 text-sm font-bold text-slate-950 hover:bg-amber-400"
            >
              Empezar a hablar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
